import React, { useEffect, useState } from "react";
import cx from "classnames";
import AdvanceCampusSelector from "../advance-campus-selector";
import AdvancedDepartmentSelector from "../advanced-department-selector";
import AdvancedModalitySelector from "../advanced-modality-selector";
import AdvancedShiftSelector from "../advanced-shift-selector";
import AdvancedProgramSelector from "../advanced-program-selector";
import AdvancedCurriculumSelector from "../advanced-curriculum-selector";
import AdvancedLevelSelector from "../advanced-level-selector";
import AdvancedSubjectSelector from "../advanced-subject-selector";
import css from "./advancedSelectorsWrapper.module.scss";
import {
  resetAdvancedSearchProperties,
  setAdvancedSearchProperty,
  useAdvancedSearchStore,
} from "@context/useAdvancedSearchStore";
import { Option } from "react-select/lib/filters";

type Selectors =
  | "campus"
  | "department"
  | "modality"
  | "shift"
  | "program"
  | "curriculum"
  | "level"
  | "course";

type SelectorsState = {
  campus: string | null;
  department: string | null;
  modality: string | null;
  shift: string | null;
  program: string | null;
  curriculum: string | null;
  level: string | null;
  course: string | null;
};

interface AdvancedSelectorsWrapperProps {
  scenario: string;
  selectors?: Array<Selectors>;
  useCurriculumCode?: boolean;
  className?: string;
  onChangeState?: (state: SelectorsState) => void;
}

const AdvancedSelectorsWrapper = ({
  scenario,
  selectors = [
    "campus",
    "department",
    "modality",
    "shift",
    "program",
    "curriculum",
    "level",
    "course",
  ],
  className = "",
  useCurriculumCode = true,
  onChangeState,
}: AdvancedSelectorsWrapperProps) => {
  const [state, setState] = useState<SelectorsState>({
    campus: null,
    department: null,
    modality: null,
    shift: null,
    program: null,
    curriculum: null,
    level: null,
    course: null,
  });

  const stateProperties = useAdvancedSearchStore(state => state.properties);

  const getUpdatedState = (
    selectsToRest: Array<
      "campus" | "department" | "modality" | "shift" | "program" | "curriculum" | "level" | "course"
    > = [],
  ) => {
    const { campus, department, modality, shift, program, curriculum, level, course } =
      stateProperties;

    resetAdvancedSearchProperties(selectsToRest);

    return {
      campus: selectsToRest.includes("campus") ? null : campus?.value,
      department: selectsToRest.includes("department") ? null : department?.value,
      modality: selectsToRest.includes("modality") ? null : modality?.value,
      shift: selectsToRest.includes("shift") ? null : shift?.value,
      program: selectsToRest.includes("program") ? null : program?.value,
      curriculum: selectsToRest.includes("curriculum") ? null : curriculum?.value,
      level: selectsToRest.includes("level") ? null : level?.value,
      course: selectsToRest.includes("course") ? null : course?.value,
    };
  };

  const handleSelectIndependet = (
    option: Option | null,
    selector: "campus" | "department" | "modality" | "shift",
  ) => {
    const updatedState = getUpdatedState(["program", "curriculum", "course"]);

    setState(prevState => {
      const newState = {
        ...prevState,
        ...updatedState,
        [selector]: option?.value,
      };

      onChangeState?.(newState);
      setAdvancedSearchProperty(selector, option);

      return newState;
    });
  };

  const handleSelectProgram = (option: Option | null) => {
    const updatedState = getUpdatedState(["curriculum", "course"]);

    setState(prevState => {
      const newState = {
        ...prevState,
        ...updatedState,
        program: option?.value,
      };

      onChangeState?.(newState);
      setAdvancedSearchProperty("program", option);

      return newState;
    });
  };

  const handleSelectCurriculum = (option: Option | null) => {
    const updatedState = getUpdatedState(["level", "course"]);

    setState(prevState => {
      const newState = {
        ...prevState,
        ...updatedState,
        curriculum: option?.value,
      };

      onChangeState?.(newState);
      setAdvancedSearchProperty("curriculum", option);

      return newState;
    });
  };

  const handleSelectLevel = (option: Option | null) => {
    const updatedState = getUpdatedState(["course"]);

    setState(prevState => {
      const newState = {
        ...prevState,
        ...updatedState,
        level: option?.value,
      };

      onChangeState?.(newState);
      setAdvancedSearchProperty("level", option);

      return newState;
    });
  };

  const handleSelectCourse = (option: Option | null) => {
    setState(prevState => {
      const newState = {
        ...prevState,
        course: option?.value,
      };

      onChangeState?.(newState);
      setAdvancedSearchProperty("course", option);

      return newState;
    });
  };

  useEffect(() => {
    const propertiesValues = Object.values(stateProperties);

    if (selectors?.length && propertiesValues.some(property => !!property)) {
      const savedState = getUpdatedState();

      setState(() => {
        onChangeState?.(savedState);
        return savedState;
      });
    }
  }, []);

  if (!selectors.length) {
    return null;
  }

  return (
    <div className={cx(css.advancedSelectorsWrapper, className)}>
      {selectors.includes("campus") && (
        <AdvanceCampusSelector
          scenario={scenario}
          campusOption={stateProperties?.campus}
          onChange={option => handleSelectIndependet(option ?? null, "campus")}
        />
      )}

      {selectors.includes("department") && (
        <AdvancedDepartmentSelector
          scenario={scenario}
          departmentOption={stateProperties?.department}
          onChange={option => handleSelectIndependet(option ?? null, "department")}
        />
      )}

      {selectors.includes("modality") && (
        <AdvancedModalitySelector
          scenario={scenario}
          modalityOption={stateProperties?.modality}
          onChange={option => handleSelectIndependet(option ?? null, "modality")}
        />
      )}

      {selectors.includes("shift") && (
        <AdvancedShiftSelector
          scenario={scenario}
          shiftOption={stateProperties?.shift}
          onChange={option => handleSelectIndependet(option ?? null, "shift")}
        />
      )}

      {selectors.includes("program") && (
        <AdvancedProgramSelector
          key={`program-${state.campus}-${state.department}-${state.modality}-${state.shift}`}
          scenario={scenario}
          campusId={state.campus}
          departmentId={state.department}
          modalityId={state.modality}
          shiftId={state.shift}
          programOption={stateProperties?.program}
          onChange={option => {
            handleSelectProgram(option ?? null);
          }}
        />
      )}

      {selectors.includes("curriculum") && (
        <AdvancedCurriculumSelector
          key={`curriculum-${state.campus}-${state.department}-${state.modality}-${state.shift}-${state.program}`}
          scenario={scenario}
          campusId={state.campus}
          departmentId={state.department}
          modalityId={state.modality}
          shiftId={state.shift}
          programId={state.program}
          curriculumOption={stateProperties?.curriculum}
          activeCode={useCurriculumCode}
          onChange={option => {
            handleSelectCurriculum(option ?? null);
          }}
        />
      )}

      {selectors.includes("level") && (
        <AdvancedLevelSelector
          scenario={scenario}
          levelOption={stateProperties?.level}
          onChange={option => {
            handleSelectLevel(option ?? null);
          }}
        />
      )}

      {selectors.includes("course") && (
        <AdvancedSubjectSelector
          key={`subject-${state.campus}-${state.department}-${state.modality}-${state.shift}-${state.program}-${state.curriculum}-${state.level}`}
          scenario={scenario}
          campusId={state.campus}
          departmentId={state.department}
          modalityId={state.modality}
          shiftId={state.shift}
          programId={state.program}
          curriculumId={state.curriculum}
          levelId={state.level}
          subjectOption={stateProperties?.course}
          onChange={option => handleSelectCourse(option ?? null)}
        />
      )}
    </div>
  );
};

export default AdvancedSelectorsWrapper;
